import { ResponseError } from '@backstage/errors';
import {
  type ApiRef,
  type DiscoveryApi,
  type FetchApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';

export type PolicyRef = { id: string; revision: string };
export type GroupScope = {
  level: 'applicationGroup';
  tenant: string;
  applicationGroupId: string;
};
export type Parameter = {
  name: string;
  value: { type: 'integer' | 'string'; integer?: number; string?: string };
};
export type ParameterSchema = {
  name: string;
  type: 'integer' | 'string';
  required: boolean;
  minimum?: number;
  maximum?: number;
};
export type PolicyRule = {
  id: string;
  expression: string;
  message: string;
  fieldPath: string;
  phases: Array<'request' | 'plan' | 'background'>;
  operations: Array<'create' | 'update' | 'delete'>;
  requiresCaller: boolean;
  requiresOldResource: boolean;
  updateBehavior: 'block' | 'allowUnchanged' | 'allowReduction';
};
export type PolicyDraft = {
  schemaVersion: '1.0';
  ref: PolicyRef;
  tenant: string;
  scope: GroupScope;
  targetKinds: ['ComputeInstance'];
  parameterSchema: ParameterSchema[];
  parameters: Parameter[];
  rules: PolicyRule[];
};
export type CheckResult = {
  valid: boolean;
  draftHash: string;
  schemaRevision?: string;
  environmentVersion: string;
  diagnostics: Array<{
    stage: string;
    ruleId?: string;
    kind?: string;
    line?: number;
    column?: number;
    message: string;
  }>;
};
export type SimulationResult = CheckResult & {
  fixtureType: 'synthetic';
  evaluations: Array<{
    ruleId: string;
    componentKey: string;
    outcome: string;
    reason: string;
    missingPaths: string[];
  }>;
};
export type SimulationInput = {
  kind: 'ComputeInstance';
  phase: 'request' | 'plan' | 'background';
  operation: 'create' | 'update' | 'delete';
  scope: {
    tenant: string;
    applicationGroupId: string;
    resourceId: string;
    stage: string;
  };
  resource: Record<string, unknown>;
};
export type AuditBinding = {
  ref: PolicyRef;
  policyRef: PolicyRef;
  scope: GroupScope;
  kinds: ['ComputeInstance'];
  stages: string[];
  operations: Array<'create' | 'update' | 'delete'>;
  mode: 'audit';
};
export type EffectiveAccess = {
  platformAdmin?: boolean;
  tenantRole?: string;
  applicationGroups?: Record<string, string>;
  capabilities?: Record<string, boolean>;
};

export interface StudioApi {
  getAccess(tenant: string, signal?: AbortSignal): Promise<EffectiveAccess>;
  check(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    signal?: AbortSignal,
  ): Promise<CheckResult>;
  simulate(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    input: SimulationInput,
    signal?: AbortSignal,
  ): Promise<SimulationResult>;
  publish(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    signal?: AbortSignal,
  ): Promise<PolicyDraft>;
  createAuditBinding(
    tenant: string,
    groupId: string,
    binding: AuditBinding,
    signal?: AbortSignal,
  ): Promise<AuditBinding>;
}

export const studioApiRef: ApiRef<StudioApi> = createApiRef<StudioApi>({
  id: 'plugin.policy.studio',
});

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function hasStudioAccess(
  value: EffectiveAccess,
  groupId: string,
): boolean {
  return (
    value.platformAdmin === true ||
    value.tenantRole === 'admin' ||
    value.applicationGroups?.[groupId] === 'admin'
  );
}

function validateScope(
  tenant: string,
  groupId: string,
  scope: GroupScope,
): void {
  if (
    !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(groupId) ||
    scope.level !== 'applicationGroup' ||
    scope.tenant !== tenant ||
    scope.applicationGroupId !== groupId
  ) {
    throw new Error('The draft must target the selected Application Group');
  }
}

function checkResult(value: unknown): value is CheckResult {
  return (
    record(value) &&
    typeof value.valid === 'boolean' &&
    typeof value.draftHash === 'string' &&
    typeof value.environmentVersion === 'string' &&
    (value.schemaRevision === undefined ||
      typeof value.schemaRevision === 'string') &&
    Array.isArray(value.diagnostics) &&
    value.diagnostics.every(
      item =>
        record(item) &&
        typeof item.stage === 'string' &&
        typeof item.message === 'string',
    )
  );
}

export class StudioClient implements StudioApi {
  constructor(
    private readonly discovery: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request(
    path: string,
    tenant: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<unknown> {
    if (!tenant) throw new Error('A tenant is required');
    const proxy = await this.discovery.getBaseUrl('proxy');
    const response = await this.fetchApi.fetch(
      `${proxy}/platform-backend/api/v1${path}?tenant=${encodeURIComponent(
        tenant,
      )}`,
      body === undefined
        ? { signal }
        : {
            method: 'POST',
            signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return response.json() as Promise<unknown>;
  }

  async getAccess(
    tenant: string,
    signal?: AbortSignal,
  ): Promise<EffectiveAccess> {
    const result = await this.request(
      '/authorization/me',
      tenant,
      undefined,
      signal,
    );
    if (
      !record(result) ||
      (result.platformAdmin !== undefined &&
        typeof result.platformAdmin !== 'boolean') ||
      (result.tenantRole !== undefined &&
        typeof result.tenantRole !== 'string') ||
      (result.applicationGroups !== undefined &&
        (!record(result.applicationGroups) ||
          !Object.values(result.applicationGroups).every(
            role => typeof role === 'string',
          ))) ||
      (result.capabilities !== undefined &&
        (!record(result.capabilities) ||
          !Object.values(result.capabilities).every(
            granted => typeof granted === 'boolean',
          )))
    ) {
      throw new Error('Invalid Studio authorization response');
    }
    return result as EffectiveAccess;
  }

  async check(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    signal?: AbortSignal,
  ): Promise<CheckResult> {
    validateScope(tenant, groupId, policy.scope);
    const result = await this.request(
      '/policies/draft-checks',
      tenant,
      { applicationGroupId: groupId, policy },
      signal,
    );
    if (!checkResult(result)) throw new Error('Invalid draft check response');
    return result;
  }

  async simulate(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    input: SimulationInput,
    signal?: AbortSignal,
  ): Promise<SimulationResult> {
    validateScope(tenant, groupId, policy.scope);
    if (
      input.scope.tenant !== tenant ||
      input.scope.applicationGroupId !== groupId
    ) {
      throw new Error(
        'Synthetic fixture must use the selected Application Group',
      );
    }
    const result = await this.request(
      '/policies/draft-simulations',
      tenant,
      { applicationGroupId: groupId, policy, input },
      signal,
    );
    const data = result as Record<string, unknown>;
    if (
      !checkResult(result) ||
      data.fixtureType !== 'synthetic' ||
      !Array.isArray(data.evaluations) ||
      !data.evaluations.every(
        (item: unknown) =>
          record(item) &&
          typeof item.ruleId === 'string' &&
          typeof item.outcome === 'string' &&
          Array.isArray(item.missingPaths),
      )
    ) {
      throw new Error('Invalid draft simulation response');
    }
    return result as SimulationResult;
  }

  async publish(
    tenant: string,
    groupId: string,
    policy: PolicyDraft,
    signal?: AbortSignal,
  ): Promise<PolicyDraft> {
    validateScope(tenant, groupId, policy.scope);
    const result = await this.request(
      '/policies/group-local',
      tenant,
      policy,
      signal,
    );
    if (
      !record(result) ||
      !record(result.ref) ||
      !record(result.scope) ||
      result.ref.id !== policy.ref.id ||
      result.ref.revision !== policy.ref.revision ||
      result.scope.applicationGroupId !== groupId ||
      result.tenant !== tenant
    ) {
      throw new Error('Invalid group-local publication response');
    }
    return result as PolicyDraft;
  }

  async createAuditBinding(
    tenant: string,
    groupId: string,
    binding: AuditBinding,
    signal?: AbortSignal,
  ): Promise<AuditBinding> {
    validateScope(tenant, groupId, binding.scope);
    if (binding.mode !== 'audit')
      throw new Error('Studio can create audit bindings only');
    const result = await this.request(
      '/policy-bindings/',
      tenant,
      binding,
      signal,
    );
    if (
      !record(result) ||
      !record(result.ref) ||
      !record(result.scope) ||
      result.ref.id !== binding.ref.id ||
      result.mode !== 'audit' ||
      result.scope.applicationGroupId !== groupId
    ) {
      throw new Error('Invalid audit binding response');
    }
    return result as AuditBinding;
  }
}
